import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { TASK_STATUSES, PRIORITIES } from "@/lib/constants";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Zap, Plus, Trash2, ArrowRight } from "lucide-react";
import { EmptyState } from "@/components/common";

const TRIGGERS = [
  { id: "task.status_changed", label: "Görev durumu değiştiğinde" },
  { id: "task.assigned", label: "Görev birine atandığında" },
  { id: "task.due_soon", label: "Görev bugün teslim olduğunda" },
  { id: "task.overdue", label: "Görev süresi geçtiğinde" },
];
const ACTION_TYPES = [
  { id: "set_status", label: "Durumu değiştir" },
  { id: "set_priority", label: "Önceliği değiştir" },
  { id: "notify", label: "Bildirim gönder" },
  { id: "chat_message", label: "Sohbet kanalına mesaj at" },
  { id: "create_subtask", label: "Alt görev oluştur" },
];
const NOTIFY_TARGETS = [
  { id: "assignees", label: "Görev sorumluları" },
  { id: "creator", label: "Görevi oluşturan" },
];
const NONE = "__none__";
const emptyForm = { name: "", enabled: true, trigger: { type: "task.status_changed", to_status: "completed" }, conditions: {}, actions: [] };
const trigLabel = (id) => TRIGGERS.find((t) => t.id === id)?.label || id;

export default function AutomationsTab() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const { data: autos = [] } = useQuery({ queryKey: ["automations"], queryFn: async () => (await api.get("/automations")).data });
  const { data: projects = [] } = useQuery({ queryKey: ["projects-min"], queryFn: async () => (await api.get("/projects")).data });
  const { data: conversations = [] } = useQuery({ queryKey: ["conversations-min"], queryFn: async () => (await api.get("/conversations")).data });

  const openNew = () => { setEditId(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (a) => {
    setEditId(a.id);
    setForm({ name: a.name, enabled: a.enabled, trigger: a.trigger || { type: "task.status_changed" }, conditions: a.conditions || {}, actions: a.actions || [] });
    setOpen(true);
  };

  const setCond = (k, v) => setForm((f) => { const c = { ...f.conditions }; if (v && v !== NONE) c[k] = v; else delete c[k]; return { ...f, conditions: c }; });
  const addAction = () => setForm((f) => ({ ...f, actions: [...f.actions, { type: "notify", target: "assignees", message: "" }] }));
  const updAction = (i, patch) => setForm((f) => ({ ...f, actions: f.actions.map((a, idx) => idx === i ? { ...a, ...patch } : a) }));
  const rmAction = (i) => setForm((f) => ({ ...f, actions: f.actions.filter((_, idx) => idx !== i) }));

  const save = async () => {
    if (!form.name.trim()) { toast.error("Kural adı gerekli"); return; }
    if (!form.actions.length) { toast.error("En az bir aksiyon ekleyin"); return; }
    for (const a of form.actions) {
      if ((a.type === "set_status" || a.type === "set_priority") && !a.value) { toast.error("Aksiyon için bir değer seçin"); return; }
      if (a.type === "chat_message" && !a.conversation_id) { toast.error("Sohbet aksiyonu için bir kanal seçin"); return; }
    }
    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.trigger.type !== "task.status_changed") delete payload.trigger.to_status;
      if (editId) await api.patch(`/automations/${editId}`, payload);
      else await api.post("/automations", payload);
      toast.success(editId ? "Otomasyon güncellendi" : "Otomasyon oluşturuldu");
      qc.invalidateQueries({ queryKey: ["automations"] });
      setOpen(false);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const toggle = async (a) => { await api.patch(`/automations/${a.id}`, { enabled: !a.enabled }); qc.invalidateQueries({ queryKey: ["automations"] }); };
  const remove = async (id) => { await api.delete(`/automations/${id}`); toast.success("Otomasyon silindi"); qc.invalidateQueries({ queryKey: ["automations"] }); };

  const actionSummary = (a) => {
    if (a.type === "set_status") return `Durumu "${TASK_STATUSES.find((s) => s.id === a.value)?.label || a.value}" yap`;
    if (a.type === "set_priority") return `Önceliği "${PRIORITIES.find((p) => p.id === a.value)?.label || a.value}" yap`;
    if (a.type === "notify") return "Bildirim gönder";
    if (a.type === "chat_message") return "Sohbete mesaj at";
    if (a.type === "create_subtask") return "Alt görev oluştur";
    return a.type;
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-muted-foreground">Bir olay gerçekleştiğinde otomatik aksiyonlar tetikleyin.</p>
        <Button onClick={openNew} data-testid="new-automation-button"><Plus className="h-4 w-4 mr-1.5" /> Yeni Kural</Button>
      </div>

      {autos.length === 0 ? (
        <EmptyState icon={Zap} title="Henüz otomasyon yok" description="İlk kuralınızı oluşturarak tekrarlayan işleri otomatikleştirin." action={<Button onClick={openNew}><Plus className="h-4 w-4 mr-1.5" /> Yeni Kural</Button>} />
      ) : (
        <div className="space-y-2.5">
          {autos.map((a) => (
            <Card key={a.id} className="p-4 flex items-center gap-4" data-testid={`automation-${a.id}`}>
              <div className={`h-9 w-9 rounded-lg grid place-items-center shrink-0 ${a.enabled ? "bg-indigo-100 text-indigo-600" : "bg-muted text-muted-foreground"}`}><Zap className="h-4 w-4" /></div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2"><p className="font-medium text-sm truncate">{a.name}</p>{a.run_count > 0 && <Badge variant="secondary" className="text-[10px]">{a.run_count}x çalıştı</Badge>}</div>
                <p className="text-xs text-muted-foreground flex items-center gap-1.5 mt-0.5 flex-wrap">
                  {trigLabel(a.trigger?.type)} <ArrowRight className="h-3 w-3" /> {(a.actions || []).map((ac) => actionSummary(ac)).join(", ")}
                </p>
              </div>
              <Switch checked={a.enabled} onCheckedChange={() => toggle(a)} data-testid={`automation-toggle-${a.id}`} />
              <Button variant="ghost" size="sm" onClick={() => openEdit(a)} data-testid={`automation-edit-${a.id}`}>Düzenle</Button>
              <AlertDialog>
                <AlertDialogTrigger asChild><button className="text-muted-foreground hover:text-rose-500" data-testid={`automation-delete-${a.id}`}><Trash2 className="h-4 w-4" /></button></AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader><AlertDialogTitle>Otomasyonu sil</AlertDialogTitle><AlertDialogDescription>Bu kural kalıcı olarak silinsin mi?</AlertDialogDescription></AlertDialogHeader>
                  <AlertDialogFooter><AlertDialogCancel>İptal</AlertDialogCancel><AlertDialogAction onClick={() => remove(a.id)}>Sil</AlertDialogAction></AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="automation-dialog">
          <DialogHeader><DialogTitle>{editId ? "Otomasyonu Düzenle" : "Yeni Otomasyon"}</DialogTitle><DialogDescription>Bir olay gerçekleştiğinde koşullara uyan görevlerde otomatik aksiyonlar çalıştırın.</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div><Label>Kural Adı</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" placeholder="Örn. Tamamlanınca ekibi bilgilendir" data-testid="automation-name-input" /></div>

            <div className="rounded-lg border p-3 space-y-3">
              <Label className="text-xs uppercase text-muted-foreground">Tetikleyici (Ne olduğunda)</Label>
              <Select value={form.trigger.type} onValueChange={(v) => setForm({ ...form, trigger: { ...form.trigger, type: v } })}>
                <SelectTrigger data-testid="automation-trigger-select"><SelectValue /></SelectTrigger>
                <SelectContent>{TRIGGERS.map((t) => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
              {form.trigger.type === "task.status_changed" && (
                <div><Label className="text-xs">Hedef durum</Label>
                  <Select value={form.trigger.to_status || "completed"} onValueChange={(v) => setForm({ ...form, trigger: { ...form.trigger, to_status: v } })}>
                    <SelectTrigger className="mt-1" data-testid="automation-tostatus-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{TASK_STATUSES.map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
            </div>

            <div className="rounded-lg border p-3 space-y-3">
              <Label className="text-xs uppercase text-muted-foreground">Koşullar (Opsiyonel)</Label>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Proje</Label>
                  <Select value={form.conditions.project_id || NONE} onValueChange={(v) => setCond("project_id", v)}>
                    <SelectTrigger className="mt-1" data-testid="automation-cond-project"><SelectValue placeholder="Tümü" /></SelectTrigger>
                    <SelectContent><SelectItem value={NONE}>Tüm projeler</SelectItem>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs">Öncelik</Label>
                  <Select value={form.conditions.priority || NONE} onValueChange={(v) => setCond("priority", v)}>
                    <SelectTrigger className="mt-1" data-testid="automation-cond-priority"><SelectValue placeholder="Tümü" /></SelectTrigger>
                    <SelectContent><SelectItem value={NONE}>Tüm öncelikler</SelectItem>{PRIORITIES.map((p) => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label className="text-xs">Etiket</Label><Input value={form.conditions.tag || ""} onChange={(e) => setCond("tag", e.target.value)} className="mt-1" placeholder="Örn. acil" data-testid="automation-cond-tag" /></div>
            </div>

            <div className="rounded-lg border p-3 space-y-3">
              <div className="flex items-center justify-between"><Label className="text-xs uppercase text-muted-foreground">Aksiyonlar (Ne yapılsın)</Label><Button size="sm" variant="outline" onClick={addAction} data-testid="add-action-button"><Plus className="h-3.5 w-3.5 mr-1" /> Aksiyon Ekle</Button></div>
              {form.actions.length === 0 && <p className="text-sm text-muted-foreground">Henüz aksiyon yok.</p>}
              {form.actions.map((a, i) => (
                <div key={i} className="rounded-lg bg-muted/50 p-2.5 space-y-2" data-testid={`action-row-${i}`}>
                  <div className="flex items-center gap-2">
                    <Select value={a.type} onValueChange={(v) => updAction(i, { type: v })}>
                      <SelectTrigger className="flex-1" data-testid={`action-type-${i}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{ACTION_TYPES.map((t) => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}</SelectContent>
                    </Select>
                    <button onClick={() => rmAction(i)} className="text-muted-foreground hover:text-rose-500 shrink-0" data-testid={`action-remove-${i}`}><Trash2 className="h-4 w-4" /></button>
                  </div>
                  {a.type === "set_status" && (
                    <Select value={a.value || ""} onValueChange={(v) => updAction(i, { value: v })}>
                      <SelectTrigger data-testid={`action-status-${i}`}><SelectValue placeholder="Durum seçin" /></SelectTrigger>
                      <SelectContent>{TASK_STATUSES.map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}</SelectContent>
                    </Select>
                  )}
                  {a.type === "set_priority" && (
                    <Select value={a.value || ""} onValueChange={(v) => updAction(i, { value: v })}>
                      <SelectTrigger data-testid={`action-priority-${i}`}><SelectValue placeholder="Öncelik seçin" /></SelectTrigger>
                      <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}</SelectContent>
                    </Select>
                  )}
                  {a.type === "notify" && (
                    <div className="space-y-2">
                      <Select value={a.target || "assignees"} onValueChange={(v) => updAction(i, { target: v })}>
                        <SelectTrigger data-testid={`action-target-${i}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{NOTIFY_TARGETS.map((t) => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}</SelectContent>
                      </Select>
                      <Input value={a.message || ""} onChange={(e) => updAction(i, { message: e.target.value })} placeholder="Bildirim mesajı (opsiyonel)" data-testid={`action-message-${i}`} />
                    </div>
                  )}
                  {a.type === "chat_message" && (
                    <div className="space-y-2">
                      <Select value={a.conversation_id || ""} onValueChange={(v) => updAction(i, { conversation_id: v })}>
                        <SelectTrigger data-testid={`action-conversation-${i}`}><SelectValue placeholder="Kanal seçin" /></SelectTrigger>
                        <SelectContent>{conversations.map((c) => <SelectItem key={c.id} value={c.id}>{c.name || "Kanal"}</SelectItem>)}</SelectContent>
                      </Select>
                      <Input value={a.message || ""} onChange={(e) => updAction(i, { message: e.target.value })} placeholder="Mesaj (opsiyonel)" data-testid={`action-chatmsg-${i}`} />
                    </div>
                  )}
                  {a.type === "create_subtask" && (
                    <Input value={a.title || ""} onChange={(e) => updAction(i, { title: e.target.value })} placeholder="Alt görev başlığı (opsiyonel)" data-testid={`action-subtask-${i}`} />
                  )}
                </div>
              ))}
            </div>

            <div className="flex items-center gap-2"><Switch checked={form.enabled} onCheckedChange={(v) => setForm({ ...form, enabled: v })} data-testid="automation-enabled-switch" /><Label>Kural aktif</Label></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>İptal</Button><Button onClick={save} disabled={saving} data-testid="automation-save-button">{editId ? "Güncelle" : "Oluştur"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
